/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { CheckOutlined } from '@ant-design/icons'
import type { ReactNode } from 'react'
import Text from 'src/components/ts-components/Text'
import { paragraphXSmall } from 'src/components/css'
import { capitalizeWords } from '@sportninja/common/ts-utils'

interface Props {
  /**
   * Position in the sequence the reader actually sees. Zones the screen never
   * asks are skipped when numbering, so nobody is shown a "step 2" without a
   * step 1.
   */
  index: number
  title: string
  subtitle?: string
  /** Answered — the chip becomes a check instead of a number. */
  complete?: boolean
  /**
   * Draws a rule above this step. Set on every zone but the first, so the
   * separator belongs to the boundary rather than to a fixed position — a zone
   * that is never asked takes its divider with it.
   */
  divided?: boolean
  /**
   * Waiting on an earlier zone. The title still renders, so the reader can see
   * what is coming, but the subtitle and body are withheld until it is their
   * turn to answer.
   */
  inert?: boolean
  children?: ReactNode
}

/**
 * One numbered question on the start step.
 *
 * A zone whose turn has not come shows its numbered title and nothing else. The
 * reader can still see the whole job ahead of them, but they are never offered
 * controls that depend on an answer they have not given — a visible division
 * grid under an unanswered "what are you registering?" invites a choice that
 * the role, once picked, may take away again.
 */
const ZoneSection = ({
  index,
  title,
  subtitle,
  complete = false,
  divided = false,
  inert = false,
  children,
}: Props) => (
  <section
    css={[sectionStyle, divided && dividedStyle, inert && inertStyle]}
    aria-disabled={inert}
  >
    <div css={headingRowStyle}>
      <span css={[stepChipStyle, (complete || !inert) && stepChipActiveStyle]}>
        {complete ? <CheckOutlined /> : index}
      </span>
      <Text variant='heading4' weight='semiBold' color={colors.WHITE}>
        {capitalizeWords(title)}
      </Text>
    </div>
    {!inert && subtitle ? (
      <div css={subtitleStyle}>
        <Text
          variant='paragraphMedium'
          weight='regular'
          color={colors.NEUTRAL_50}
        >
          {subtitle}
        </Text>
      </div>
    ) : null}
    {!inert && children ? <div css={bodyStyle}>{children}</div> : null}
  </section>
)

export default ZoneSection

const sectionStyle = css`
  display: flex;
  flex-direction: column;
  width: 100%;
`

/* The rule sits above the step, with the stack's 24px gap above it and this
   24px below, so it reads as centred between two steps. */
const dividedStyle = css`
  border-top: 1px solid ${colors.NEUTRAL_600};
  padding-top: 24px;
`

const inertStyle = css`
  opacity: 0.45;
  pointer-events: none;
`

const headingRowStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
`

const stepChipStyle = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: ${colors.NEUTRAL_600};
  color: ${colors.NEUTRAL_40};
  /* The preset rather than a raw size — it carries the tenant font with it. */
  ${paragraphXSmall}
`

const stepChipActiveStyle = css`
  background-color: ${colors.PRIMARY};
  color: ${colors.PRIMARY_TEXT};
`

/* 8px under the title, matching the old PageTitle's column gap; indented past
   the step chip so it lines up with the title rather than the number. */
const subtitleStyle = css`
  margin-top: 8px;
  margin-left: 36px;
`

/* 24px before the content, matching the old PageTitle's margin-bottom. */
const bodyStyle = css`
  margin-top: 24px;
`
